import type { Duration, IOMapping, Checkpoint } from '../../graph/domain/types';
import type { Store } from '../types';
import type { RecommendedInstrumentRef } from '../../components/modals/AddStepModal/types';
import { propagateComplexComposition, propagateComplexCompositionWithReminders } from '../compositionService';
import { buildInputReminderNode, computeOutputQuantityFromMapping, computeInputReminderQuantity } from './complexHelpers';
import { cloneGraphData } from './snapshots';
import { propagateDownstream } from '@/commands/helpers/propagateDownstream';

type GetState = () => Store;

// ── Public types ────────────────────────────────────────────────────────────

export interface AddComplexStepInput {
  sourceMaterialId: string;
  operationTypeId: string;
  operationDisplayName: string;
  dataExpected?: boolean;
  additionalInputIds?: string[];
  outputMaterialIds: string[];
  ioMapping?: IOMapping[];
  parameters: Record<string, unknown>;
  duration?: Duration;
  notes?: string;
  comments?: string;
  recommendedInstrument?: RecommendedInstrumentRef[];
  checkpoints?: Checkpoint[];
  operationTemplate?: {
    operationTypeId: string;
    parameters?: Array<{ parameterName?: string; value?: unknown; unit?: string; fieldType?: string; required?: boolean; tags?: string[] }>;
    hideParamsMap?: Record<string, boolean>;
    translationTemplate?: string;
  };
  approvers?: any;
  notifications?: any;
}

export interface AddComplexStepResult {
  operationId: string;
}

// ── Positioning constants ───────────────────────────────────────────────────

const SOURCE_TO_OPERATION_DY = 120;
const OPERATION_TO_REMINDER_DY = 120;
const REMINDER_SPREAD_DX = 80;

// ── Factory ─────────────────────────────────────────────────────────────────

export function createComplexActions(get: GetState) {
  return {
    addComplexStep(input: AddComplexStepInput): AddComplexStepResult {
      const oldGraphData = cloneGraphData(get().graphData);

      const {
        sourceMaterialId,
        additionalInputIds = [],
        outputMaterialIds,
        ioMapping = [],
      } = input;

      // Validate source exists
      const source = get().graphData.materialNodes[sourceMaterialId];
      if (!source) {
        throw new Error(`Source material not found: ${sourceMaterialId}`);
      }

      // Validate additional inputs exist
      for (const id of additionalInputIds) {
        if (!get().graphData.materialNodes[id]) {
          throw new Error(`Input material not found: ${id}`);
        }
      }

      // Validate outputs exist (if provided)
      // Outputs are optional, similar to Transform & Measure operations
      for (const id of outputMaterialIds) {
        if (!get().graphData.materialNodes[id]) {
          throw new Error(`Output material not found: ${id}`);
        }
      }

      // Position the operation below the source
      const sourcePos = source.position ?? { x: 0, y: 0 };
      const opPos = {
        x: sourcePos.x,
        y: sourcePos.y + SOURCE_TO_OPERATION_DY,
      };

      // Compute correct global creationOrder (max existing + 1)
      let maxOrder = 0;
      for (const op of Object.values(get().graphData.operationNodes)) {
        maxOrder = Math.max(maxOrder, op.creationOrder ?? 0);
      }
      const nextOrder = maxOrder + 1;

      // 1. Create the operation node
      const operationId = get().addOperationNode({
        archetype: 'COMPLEX',
        operationTypeId: input.operationTypeId,
        displayName: input.operationDisplayName,
        dataExpected: input.dataExpected ?? false,
        parameters: input.parameters,
        checkPoints: input.checkpoints || [],
        duration: input.duration,
        notes: input.notes,
        comments: input.comments,
        recommendedInstrument: input.recommendedInstrument,
        operationTemplate: input.operationTemplate,
        approvers: input.approvers,
        notifications: input.notifications,
        creationOrder: nextOrder,
        position: opPos,
        complexOptions: { ioMapping: Array.isArray(ioMapping) ? ioMapping : [] },
      });

      // 2. Edges: source + additional inputs → operation
      get().connectMaterialToOperation(sourceMaterialId, operationId);
      for (const id of additionalInputIds) {
        get().connectMaterialToOperation(id, operationId);
      }

      // 3. Edges: operation → each output
      for (const id of outputMaterialIds) {
        get().connectOperationToMaterial(operationId, id);
      }

      // Prepare input materials map for quantity calculations
      const allInputIds = [sourceMaterialId, ...additionalInputIds];
      const allInputNames = allInputIds.map(id => get().graphData.materialNodes[id].name);
      const inputMaterialsMap = Object.fromEntries(
        allInputIds.map(id => [id, get().graphData.materialNodes[id]])
      );
      const outputMaterialsMap = Object.fromEntries(
        outputMaterialIds.map(id => [id, get().graphData.materialNodes[id]])
      );

      // 3.5 Calculate and update output quantities based on IO mapping
      for (const outputId of outputMaterialIds) {
        const outputQty = computeOutputQuantityFromMapping(outputId, ioMapping, inputMaterialsMap, outputMaterialsMap);
        if (outputQty) {
          get().updateMaterialNode(outputId, { quantity: outputQty });
        }
      }

      // 4. Create reminder nodes and set composition
      const { outputComposition, reminderCompositions } = propagateComplexCompositionWithReminders(
        allInputIds,
        allInputNames,
        operationId,
      );

      // Update output materials with composition
      for (const id of outputMaterialIds) {
        get().updateMaterialNode(id, { composition: outputComposition });
      }

      // 5. Create reminder nodes (positioned below operation, spread horizontally)
      const reminderStartX = opPos.x - (allInputIds.length * REMINDER_SPREAD_DX) / 2;
      const reminderY = opPos.y + OPERATION_TO_REMINDER_DY;

      for (let i = 0; i < allInputIds.length; i++) {
        const inputId = allInputIds[i];
        const inputMaterial = get().graphData.materialNodes[inputId];

        // Build reminder node spec
        const reminderSpec = buildInputReminderNode(inputMaterial, operationId);
        
        // Calculate remainder quantity
        const reminderQty = computeInputReminderQuantity(inputId, ioMapping, inputMaterial);
        if (reminderQty) {
          reminderSpec.quantity = reminderQty;
        }
        
        reminderSpec.position = {
          x: reminderStartX + i * REMINDER_SPREAD_DX,
          y: reminderY,
        };

        // Create reminder node
        const reminderId = get().addMaterialNode(reminderSpec);

        // Edge: operation → reminder
        get().connectOperationToMaterial(operationId, reminderId);

        // Set composition on reminder
        get().updateMaterialNode(reminderId, { composition: reminderCompositions[i] });
      }

      // Recalculate downstream of each selected output so existing dependent
      // steps stay in sync immediately after save.
      const graphDataForPropagation = cloneGraphData(get().graphData);
      const changedMaterialIds = new Set<string>();
      for (const outputId of outputMaterialIds) {
        const result = propagateDownstream(graphDataForPropagation, outputId, {
          fields: { identity: true, quantity: true, constituents: true },
          oldGraphData,
        });
        for (const changedId of result.changedMaterialIds) {
          changedMaterialIds.add(changedId);
        }
      }

      for (const changedId of changedMaterialIds) {
        const updated = graphDataForPropagation.materialNodes[changedId];
        if (!updated) continue;
        get().updateMaterialNode(changedId, {
          name: updated.name,
          container: updated.container,
          constituents: updated.constituents,
          quantity: updated.quantity,
          symbolicQuantity: updated.symbolicQuantity,
          isReminder: updated.isReminder,
        });
      }

      return { operationId };
    },
  };
}
